import { computed, signal } from '@angular/core';

import { ApiError } from '@core/http/api-error';
import { apiCodeOf, apiMessageOf, errorMessageOf, fieldErrorsOf } from '../utils/api-error.util';

/**
 * ActionState — the per-action lifecycle primitive for /v2.
 *
 * One instance models ONE asynchronous action's `idle → loading → success →
 * error` lifecycle with signals, and {@link run} both guards against
 * duplicate execution (a second call while in flight is ignored) and captures
 * failures. This is the reusable shape behind the project rule that *every* API
 * action owns its own loading + error state — never a single shared flag.
 *
 * Usage in a facade (one line per action):
 * ```ts
 * readonly saveProfile = new ActionState();
 * async save(dto: ProfileDto): Promise<void> {
 *   await this.saveProfile.run(() => firstValueFrom(this.repo.save(dto)));
 * }
 * ```
 * Usage in a template:
 * ```html
 * <tb-button [loading]="facade.saveProfile.loading()" (pressed)="facade.save()" />
 * @if (facade.saveProfile.failed()) { <tb-state-block tone="error" (retry)="facade.save()" /> }
 * ```
 *
 * Plain class (not `@Injectable`) so a facade can own many instances as fields;
 * no DI, no platform access ⇒ SSR-safe by construction.
 */
export class ActionState {
  /**
   * @param fallbackKey i18n key for THIS action's own "it didn't work" copy,
   *   shown when the backend sent no `response.message`. Naming the failed
   *   request ("couldn't load your invoices") beats one generic line for every
   *   call. Optional — omit it and callers keep using their own fallback.
   * @param label short name for this action in logs (e.g. `POST /subscription/trial`).
   */
  constructor(
    readonly fallbackKey: string | null = null,
    readonly label: string = '',
  ) {}

  private readonly _loading = signal(false);
  private readonly _failed = signal(false);
  /** Localized message when the failure was a {@link ApiError}; else null. */
  private readonly _error = signal<string | null>(null);
  /** The backend's OWN `response.message`; null when it sent none. */
  private readonly _apiMessage = signal<string | null>(null);
  /** The backend's machine-readable refusal code (`data.code`); null when none. */
  private readonly _code = signal<string | null>(null);
  /** HTTP status of the last failure (0 for network/timeout); null when none. */
  private readonly _status = signal<number | null>(null);
  /** Laravel `422` per-field messages, flattened; empty for every other failure. */
  private readonly _fieldErrors = signal<readonly string[]>([]);
  private readonly _succeeded = signal(false);

  /** True while the action is in flight. Drives skeletons / button spinners. */
  readonly loading = this._loading.asReadonly();
  /** True after the last run rejected. Unambiguous trigger for error UI. */
  readonly failed = this._failed.asReadonly();
  /** Localized error message (API message, else the HTTP layer's per-status default). */
  readonly error = this._error.asReadonly();
  /**
   * ONLY the backend's `response.message` — null when the API sent none. Pair
   * with {@link fallbackKey} to show request-specific copy instead of a generic
   * line: `apiMessage() ?? translate(fallbackKey)`.
   */
  readonly apiMessage = this._apiMessage.asReadonly();
  /**
   * The backend's stable refusal code (`data.code`, e.g. `programme.pre_assessments_required`)
   * — branch on this, never on the localized {@link apiMessage}. Null when the body sent none.
   */
  readonly code = this._code.asReadonly();
  /** HTTP status of the last failure — lets a caller branch on 422/403/429. */
  readonly status = this._status.asReadonly();
  /**
   * The backend's per-FIELD `422` messages (empty otherwise) — see
   * {@link fieldErrorsOf}. Render these ALONGSIDE {@link error} so a validation
   * failure names the offending field instead of showing one generic line.
   */
  readonly fieldErrors = this._fieldErrors.asReadonly();
  /** True after the last run resolved (cleared when a new run starts). */
  readonly succeeded = this._succeeded.asReadonly();
  /** Neither loading nor failed (fresh, or last run succeeded). */
  readonly idle = computed(() => !this._loading() && !this._failed());

  /**
   * Run `op` under this lifecycle. Returns its result, or `null` when the call
   * was skipped (already in flight ⇒ duplicate guard) or failed. Callers that
   * must distinguish "failed" from a legitimate `null` result can read
   * {@link failed} / {@link succeeded} after awaiting.
   */
  async run<T>(op: () => Promise<T>): Promise<T | null> {
    if (this._loading()) return null; // duplicate-execution guard
    this._loading.set(true);
    this._failed.set(false);
    this._succeeded.set(false);
    this._error.set(null);
    this._apiMessage.set(null);
    this._code.set(null);
    this._status.set(null);
    this._fieldErrors.set([]);
    try {
      const result = await op();
      this._succeeded.set(true);
      return result;
    } catch (err: unknown) {
      this._failed.set(true);
      this._error.set(errorMessageOf(err));
      this._apiMessage.set(apiMessageOf(err));
      this._code.set(apiCodeOf(err));
      this._status.set(err instanceof ApiError ? err.httpStatus : null);
      this._fieldErrors.set(fieldErrorsOf(err));
      return null;
    } finally {
      this._loading.set(false);
    }
  }

  /** Clear the error/failed flags (e.g. when the user dismisses an error). */
  reset(): void {
    this._failed.set(false);
    this._error.set(null);
    this._apiMessage.set(null);
    this._code.set(null);
    this._status.set(null);
    this._fieldErrors.set([]);
  }
}
